/**
 * The setup steps, described once.
 *
 * Splitting analysis setup into steps was not enough on its own: a step named
 * "Categories and criteria / what and how to score" still leaves the two
 * questions a person actually has — what happens if I never open it, and where
 * do I see the effect. Both answers live here, next to the step, so the
 * pipeline strip, the overview map, and each step's own intro cannot drift
 * into three different explanations of the same thing.
 *
 * Every step already works untouched; configuring it makes the analysis
 * sharper. That is why each entry has a `fallbackKey` — the sentence naming the
 * default — rather than a "required" flag.
 */

import { getCrmDeliverySupport } from '@aica/core';

export type PipelineStepId =
  | 'routing'
  | 'criteria'
  | 'call-criteria'
  | 'scripts'
  | 'sales-methodology'
  | 'crm-updates';

export interface PipelineStepMeta {
  id: PipelineStepId;
  to: string;
  /** Step name, e.g. "Routing". */
  labelKey: string;
  /** Two-word hint under the name, e.g. "record → project". */
  subKey: string;
  /** One sentence: what decision this step owns. */
  decidesKey: string;
  /** The same sentence for a source that only gets the deal-state comment. */
  commentOnlyDecidesKey?: string;
  /** One sentence: what happens if it is never opened. */
  fallbackKey: string;
  /**
   * The same sentence for a source that takes no structured field writes
   * (Uspacy: the report goes into the call note, and the deal-state comment
   * into the timeline). See `stepsForVendor`.
   */
  commentOnlyFallbackKey?: string;
  /** One sentence: where the result of this step becomes visible. */
  visibleKey: string;
  /** The same sentence for a source that only gets the deal-state comment. */
  commentOnlyVisibleKey?: string;
}

export const PIPELINE_STEPS: readonly PipelineStepMeta[] = [
  {
    id: 'routing',
    to: '/analysis-setup/routing',
    labelKey: 'steps.routing',
    subKey: 'steps.routingSub',
    decidesKey: 'stepDetails.routing.decides',
    fallbackKey: 'stepDetails.routing.fallback',
    visibleKey: 'stepDetails.routing.visible',
  },
  {
    id: 'criteria',
    to: '/analysis-setup/categories',
    labelKey: 'steps.criteria',
    subKey: 'steps.criteriaSub',
    decidesKey: 'stepDetails.criteria.decides',
    fallbackKey: 'stepDetails.criteria.fallback',
    visibleKey: 'stepDetails.criteria.visible',
  },
  {
    // Custom call criteria (#861). Exists only for an organization with
    // `enableCallCriteria` — `useVisibleSteps` hides it otherwise.
    id: 'call-criteria',
    to: '/analysis-setup/call-criteria',
    labelKey: 'steps.callCriteria',
    subKey: 'steps.callCriteriaSub',
    decidesKey: 'stepDetails.callCriteria.decides',
    fallbackKey: 'stepDetails.callCriteria.fallback',
    visibleKey: 'stepDetails.callCriteria.visible',
  },
  {
    id: 'scripts',
    to: '/analysis-setup/scripts',
    labelKey: 'steps.scripts',
    subKey: 'steps.scriptsSub',
    decidesKey: 'stepDetails.scripts.decides',
    fallbackKey: 'stepDetails.scripts.fallback',
    visibleKey: 'stepDetails.scripts.visible',
  },
  {
    // The sales methodology of each CRM pipeline (#766, #2491). Exists only
    // behind the deal-state gate — `useVisibleSteps` hides it otherwise. Before
    // CRM updates: the deal-state comment there is built by this methodology.
    id: 'sales-methodology',
    to: '/analysis-setup/sales-methodology',
    labelKey: 'steps.salesMethodology',
    subKey: 'steps.salesMethodologySub',
    decidesKey: 'stepDetails.salesMethodology.decides',
    fallbackKey: 'stepDetails.salesMethodology.fallback',
    visibleKey: 'stepDetails.salesMethodology.visible',
  },
  {
    id: 'crm-updates',
    to: '/analysis-setup/crm-updates',
    labelKey: 'steps.crm',
    subKey: 'steps.crmSub',
    decidesKey: 'stepDetails.crm.decides',
    commentOnlyDecidesKey: 'stepDetails.crm.decidesCommentOnly',
    fallbackKey: 'stepDetails.crm.fallback',
    commentOnlyFallbackKey: 'stepDetails.crm.fallbackCommentOnly',
    visibleKey: 'stepDetails.crm.visible',
    commentOnlyVisibleKey: 'stepDetails.crm.visibleCommentOnly',
  },
] as const;

/**
 * Does the CRM updates step exist for this source? The ONE rule: the hub's
 * strip and overview map, the step counter, the step's own route and the hub's
 * status chip all ask it, so they cannot disagree about whether the step is
 * there.
 *
 * It applies when the source takes structured field writes (Bitrix24), or when
 * its CRM takes the analysis report in its timeline without them (Uspacy — the
 * report as a comment and as a note, which the step can switch off; spec
 * #2979, decision 5). The deal-state comment no longer decides whether the step
 * exists: only its own row sits behind the plan's gate. Both facts are catalog
 * capabilities, not vendor names — a CRM that takes the deal-state comment
 * takes the timeline report too.
 */
export function crmUpdatesStepApplies(vendor: string | null | undefined): boolean {
  const support = getCrmDeliverySupport(vendor ?? '');
  return support.structuredFields || support.dealStateComment;
}

/** The source takes no field writes: the step is the timeline actions only (Uspacy). */
export function isCommentOnlyCrmVendor(vendor: string | null | undefined): boolean {
  const support = getCrmDeliverySupport(vendor ?? '');
  return support.dealStateComment && !support.structuredFields;
}

/**
 * The steps as worded for the active source. The CRM updates sentences
 * describe field writes and the call transcript, which a source without
 * structured fields (Uspacy) never gets; it reads its own variants instead —
 * the analysis report appended to the call note, plus the deal-state comment.
 * A source with neither capability, and `undefined` (the source is not known
 * yet), keep the standard wording rather than guess.
 */
export function stepsForVendor(
  steps: readonly PipelineStepMeta[],
  vendor: string | null | undefined
): readonly PipelineStepMeta[] {
  if (vendor === undefined || !isCommentOnlyCrmVendor(vendor)) return steps;
  return steps.map((step) =>
    step.commentOnlyDecidesKey ||
    step.commentOnlyFallbackKey ||
    step.commentOnlyVisibleKey
      ? {
          ...step,
          decidesKey: step.commentOnlyDecidesKey ?? step.decidesKey,
          fallbackKey: step.commentOnlyFallbackKey ?? step.fallbackKey,
          visibleKey: step.commentOnlyVisibleKey ?? step.visibleKey,
        }
      : step
  );
}

/** 1-based position, for "Step 2 of 4". */
export function stepPosition(id: PipelineStepId): number {
  return PIPELINE_STEPS.findIndex((step) => step.id === id) + 1;
}
